import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface TocItem {
  id: string;
  label: string;
}

interface ArticleTemplateProps {
  headline: string;
  datePublished: string;
  dateModified: string;
  readTime: string;
  author: string;
  tocItems: TocItem[];
  schemaData: Record<string, unknown>;
  faqSchemaData?: Record<string, unknown>;
  children: React.ReactNode;
}

export default function ArticleTemplate({
  headline,
  datePublished,
  dateModified,
  readTime,
  author,
  tocItems,
  schemaData,
  faqSchemaData,
  children,
}: ArticleTemplateProps) {
  return (
    <>
      <Navbar />
      <main>
        {/* Article Hero */}
        <section className="w-full bg-background-50 pt-24 pb-12 md:pt-32 md:pb-16">
          <div className="w-full px-6 md:px-10 max-w-[1400px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
              {/* Left: Title & Metadata */}
              <div className="lg:w-[58%]">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-accent-500 hover:text-accent-600 font-medium mb-6 transition-colors"
                >
                  <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                  Back to Home
                </Link>
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-[1.08]">
                  {headline}
                </h1>
                <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                  <span>Updated {dateModified}</span>
                  <span>&middot;</span>
                  <span>{readTime} min read</span>
                  <span>&middot;</span>
                  <span>By {author}</span>
                </div>
              </div>

              {/* Right: TOC */}
              <div className="lg:w-[38%]">
                <div className="bg-background-100 rounded-2xl p-6 border border-background-200/70 sticky top-24">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4">Table of Contents</h3>
                  <nav className="flex flex-col gap-2.5">
                    {tocItems.map((item, i) => (
                      <a
                        key={item.id}
                        href={`#${item.id}`}
                        className="text-sm text-foreground-500 hover:text-foreground-950 transition-colors"
                      >
                        {i + 1}. {item.label}
                      </a>
                    ))}
                  </nav>
                  <Link
                    to="/"
                    className="mt-6 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 border border-accent-200 text-accent-600 text-sm font-medium rounded-full hover:bg-accent-50 transition-all whitespace-nowrap"
                  >
                    <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
                    Return to Homepage
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="w-full bg-background-50 pb-20 md:pb-28">
          <div className="w-full px-6 md:px-10 max-w-[1400px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
              {/* Main Content */}
              <div className="lg:w-[58%] prose-custom">
                {children}
              </div>
              {/* Sidebar spacer */}
              <div className="hidden lg:block lg:w-[38%]"></div>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(schemaData),
        }}
      />
      {faqSchemaData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchemaData),
          }}
        />
      )}
    </>
  );
}